import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Search, RefreshCw, Download, Filter, AlertTriangle, CheckCircle, User, FileText, Mail, Settings, Shield, Clock, ArrowLeft, Crown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLogs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [searchEmail, setSearchEmail] = useState('');
  const [dateRange, setDateRange] = useState('7'); // days
  const [impersonatedOnly, setImpersonatedOnly] = useState(false);

  // Check if user is super admin
  if (user?.role !== 'super_admin' && user?.role !== 'master_admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <Shield className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-slate-500">Only Super Admins can view audit logs.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '25'
      });
      
      if (selectedTenant && selectedTenant !== 'all') {
        params.append('tenant_id', selectedTenant);
      }
      if (selectedEventType && selectedEventType !== 'all') {
        params.append('event_type', selectedEventType);
      }
      if (searchEmail) {
        params.append('user_email', searchEmail);
      }
      if (dateRange) {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(dateRange));
        params.append('start_date', startDate.toISOString());
      }
      if (impersonatedOnly) {
        params.append('impersonated_only', 'true');
      }

      const response = await fetch(`${API_URL}/api/super-admin/audit-logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch logs');
      
      const data = await response.json();
      setLogs(data.logs || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/super-admin/audit-logs/stats?days=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/super-admin/audit-logs/event-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEventTypes(data.event_types || []);
      }
    } catch (error) {
      console.error('Error fetching event types:', error);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/super-admin/tenants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTenants(data || []);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchEventTypes();
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, selectedTenant, selectedEventType, dateRange, impersonatedOnly]);

  const handleSearch = () => {
    setPage(1);
    fetchLogs();
  };

  const getEventIcon = (eventType) => {
    if (eventType?.includes('login')) return <User className="w-4 h-4" />;
    if (eventType?.includes('page')) return <FileText className="w-4 h-4" />;
    if (eventType?.includes('newsletter') || eventType?.includes('subscriber')) return <Mail className="w-4 h-4" />;
    if (eventType?.includes('settings') || eventType?.includes('2fa')) return <Settings className="w-4 h-4" />;
    if (eventType?.includes('impersonate')) return <Shield className="w-4 h-4" />;
    return <Clock className="w-4 h-4" />;
  };

  const getEventBadgeColor = (eventType) => {
    if (eventType?.includes('failed') || eventType?.includes('deleted')) return 'destructive';
    if (eventType?.includes('created') || eventType?.includes('success')) return 'default';
    if (eventType?.includes('impersonate')) return 'secondary';
    return 'outline';
  };

  const formatEventType = (eventType) => {
    return eventType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-logs-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Audit Logs</h1>
            <p className="text-slate-500">Monitor tenant activity across the platform</p>
          </div>
        </div>
        <Button onClick={() => { fetchLogs(); fetchStats(); }} variant="outline" data-testid="refresh-logs">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Events</p>
                  <p className="text-2xl font-bold">{stats.total_events?.toLocaleString()}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-500" />
              </div>
              <p className="text-xs text-slate-400 mt-2">Last {dateRange} days</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Failed Logins</p>
                  <p className="text-2xl font-bold text-red-500">{stats.failed_logins?.toLocaleString()}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-xs text-slate-400 mt-2">Security events</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-slate-500 mb-2">Top Events</p>
                <div className="space-y-1">
                  {stats.event_breakdown?.slice(0, 3).map((e, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-600 truncate">{formatEventType(e.event_type)}</span>
                      <span className="font-medium">{e.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-slate-500 mb-2">Most Active Tenants</p>
                <div className="space-y-1">
                  {stats.most_active_tenants?.slice(0, 3).map((t, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-600 truncate">{t.tenant_name}</span>
                      <span className="font-medium">{t.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <label className="text-sm font-medium mb-1 block">Tenant</label>
              <Select value={selectedTenant} onValueChange={(v) => { setSelectedTenant(v); setPage(1); }}>
                <SelectTrigger data-testid="tenant-filter">
                  <SelectValue placeholder="All Tenants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {tenants.map(t => (
                    <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-48">
              <label className="text-sm font-medium mb-1 block">Event Type</label>
              <Select value={selectedEventType} onValueChange={(v) => { setSelectedEventType(v); setPage(1); }}>
                <SelectTrigger data-testid="event-type-filter">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {eventTypes.map(et => (
                    <SelectItem key={et} value={et}>{formatEventType(et)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-32">
              <label className="text-sm font-medium mb-1 block">Time Range</label>
              <Select value={dateRange} onValueChange={(v) => { setDateRange(v); setPage(1); }}>
                <SelectTrigger data-testid="date-range-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24h</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-1 block">Search by Email</label>
              <div className="flex gap-2">
                <Input
                  placeholder="user@example.com"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="email-search"
                />
                <Button onClick={handleSearch} variant="secondary">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex items-end">
              <label
                className="flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-slate-50 text-sm font-medium"
                data-testid="impersonated-only-toggle"
              >
                <input
                  type="checkbox"
                  checked={impersonatedOnly}
                  onChange={(e) => { setImpersonatedOnly(e.target.checked); setPage(1); }}
                  className="w-4 h-4 accent-amber-600"
                />
                <Shield className="w-4 h-4 text-amber-600" />
                Impersonated only
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Activity Log</CardTitle>
              <CardDescription>{total.toLocaleString()} events found</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No audit logs found matching your filters.</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Timestamp</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log, idx) => (
                    <TableRow
                      key={log.log_id || idx}
                      className={
                        log.impersonated_by_email
                          ? (log.impersonated_by_role === 'master_admin'
                              ? 'bg-rose-50/40 hover:bg-rose-50'
                              : 'bg-amber-50/40 hover:bg-amber-50')
                          : ''
                      }
                      data-testid={`audit-row-${log.log_id || idx}`}
                    >
                      <TableCell className="text-sm text-slate-500">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-sm">{log.tenant_name || 'Unknown'}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getEventBadgeColor(log.event_type)} className="flex items-center gap-1 w-fit">
                          {getEventIcon(log.event_type)}
                          {formatEventType(log.event_type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex flex-col gap-0.5">
                          <span>{log.user_email || '-'}</span>
                          {log.impersonated_by_email && (() => {
                            const isMaster = log.impersonated_by_role === 'master_admin';
                            const RoleIcon = isMaster ? Crown : Shield;
                            const label = isMaster ? 'master admin' : 'super admin';
                            const colorClass = isMaster
                              ? 'text-rose-700'
                              : 'text-amber-700';
                            return (
                              <span
                                className={`inline-flex items-center gap-1 text-[11px] font-medium ${colorClass}`}
                                title={`This action was performed by ${label} ${log.impersonated_by_email} while impersonating ${log.user_email}`}
                                data-testid="impersonated-by-badge"
                              >
                                <RoleIcon className="w-3 h-3" />
                                via {label}: {log.impersonated_by_email}
                              </span>
                            );
                          })()}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[300px] truncate">
                        {log.event_description}
                      </TableCell>
                      <TableCell className="text-sm text-slate-400">
                        {log.ip_address || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;
